import { useMutation, useQuery } from '@tanstack/react-query'
import type {
  RegistrationFormTemplateSingleResponse,
  RegistrationFormTemplatesListResponse
} from '@type/registrationForm.type'
import { AXIOS_INSTANCE } from '../api'
import { queryClient } from '../store/provider/TanStackProvider'

type Props = {
  page?: number
  limit?: number
  query?: string
  category?: string
  code?: string
  id?: string
}

export const useRegistrationFormTemplate = (arg: Props = {}) => {
  const { page = 1, limit = 100, query, category, code, id } = arg

  const { data, isLoading } = useQuery<RegistrationFormTemplatesListResponse>({
    enabled: !id,
    queryKey: ['registration_form_templates', { page, limit, query, category, code }],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<RegistrationFormTemplatesListResponse>('/registration_form_templates', {
        params: {
          'page[number]': page,
          'page[size]': limit,
          ...(query && { query }),
          ...(category && { category }),
          ...(code && { code })
        }
      })
      return data
    }
  })

  const { data: singleTemplate, isLoading: singleTemplateIsLoading } = useQuery<
    RegistrationFormTemplateSingleResponse | undefined
  >({
    enabled: !!id,
    queryKey: ['registration_form_templates', { id }],
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<RegistrationFormTemplateSingleResponse>(
        `/registration_form_templates/${id as string}`
      )
      return data
    }
  })

  const { mutateAsync: createTemplate } = useMutation({
    mutationFn: async (formVal: unknown) => {
      const { data } = await AXIOS_INSTANCE.post('/registration_form_templates', formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registration_form_templates'] })
    }
  })

  const { mutateAsync: updateTemplate } = useMutation({
    mutationFn: async ({ id: templateId, formVal }: { id: string; formVal: unknown }) => {
      const { data } = await AXIOS_INSTANCE.patch(`/registration_form_templates/${templateId}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['registration_form_templates'] })
      queryClient.invalidateQueries({ queryKey: ['registration_form_templates', { id: variables.id }] })
    }
  })

  const { mutateAsync: softDeleteTemplate } = useMutation({
    mutationFn: async (templateId: string) => {
      const { data } = await AXIOS_INSTANCE.delete(`/registration_form_templates/${templateId}`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registration_form_templates'] })
    }
  })

  const { mutateAsync: fetchShare } = useMutation({
    mutationFn: async (templateId: string) => {
      const { data } = await AXIOS_INSTANCE.get<{ data: { share_token: string; share_path: string } }>(
        `/registration_form_templates/${templateId}/share`
      )
      return data.data
    }
  })

  return {
    data,
    pagination: data?.meta.pagination,
    isLoading,
    singleTemplate,
    singleTemplateIsLoading,
    createTemplate,
    updateTemplate,
    softDeleteTemplate,
    fetchShare
  }
}
